import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Paper, Switch, FormControlLabel,
  TextField, Button, Alert, CircularProgress, Divider, Chip, Grid
} from '@mui/material';
import { ScienceOutlined as DemoIcon, AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = '/api/config';

function DemoSettings() {
  const [settings, setSettings]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [success, setSuccess]     = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_BASE}/demo-settings`);
      setSettings(res.data.demoSettings);
    } catch (err) {
      setError('Failed to load demo settings. ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);
      const res = await axios.put(`${API_BASE}/demo-settings`, settings);
      setSettings(res.data.demoSettings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save demo settings. ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const update = (path, value) => {
    setSettings(prev => {
      const next = { ...prev };
      const keys = path.split('.');
      let ref = next;
      for (let i = 0; i < keys.length - 1; i++) {
        ref[keys[i]] = { ...ref[keys[i]] };
        ref = ref[keys[i]];
      }
      ref[keys[keys.length - 1]] = value;
      return next;
    });
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DemoIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>Demo Mode Settings</Typography>
        <Chip label={settings?.enabled ? 'ENABLED' : 'DISABLED'} color={settings?.enabled ? 'success' : 'error'} sx={{ ml: 1 }} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Control the "Try Demo" feature shown to users before account creation. Disable it entirely, or configure each demo user type independently.
      </Typography>

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>Demo settings saved successfully.</Alert>}

      {/* Master Toggle */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FormControlLabel
          control={<Switch checked={!!settings?.enabled} onChange={e => update('enabled', e.target.checked)} color="primary" />}
          label={<><Typography fontWeight={600}>Enable "Try Demo" Feature</Typography><Typography variant="caption" display="block" color="text.secondary">When OFF, the demo button is completely hidden from the iOS welcome screen.</Typography></>}
        />
      </Paper>

      <Grid container spacing={3}>
        {/* Regular Demo User */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <DemoIcon color="info" />
              <Typography variant="h6" fontWeight={600}>Regular Demo User</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Available to any user who wants to preview the app before signing up.
            </Typography>
            <FormControlLabel sx={{ mb: 2 }} control={<Switch checked={!!settings?.regularDemo?.enabled} onChange={e => update('regularDemo.enabled', e.target.checked)} />} label="Enable" />
            <TextField fullWidth label="Username" value={settings?.regularDemo?.username || ''} onChange={e => update('regularDemo.username', e.target.value)} sx={{ mb: 2 }} size="small" />
            <TextField fullWidth label="Password" value={settings?.regularDemo?.password || ''} onChange={e => update('regularDemo.password', e.target.value)} sx={{ mb: 2 }} size="small" />
            <TextField fullWidth label="Display Name" value={settings?.regularDemo?.displayName || ''} onChange={e => update('regularDemo.displayName', e.target.value)} size="small" />
          </Paper>
        </Grid>

        {/* Reviewer / Admin Demo User */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AdminIcon color="warning" />
              <Typography variant="h6" fontWeight={600}>Reviewer / Admin User</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              For Apple App Store reviewers and enterprise testing. Disable after review is approved.
            </Typography>
            <FormControlLabel sx={{ mb: 2 }} control={<Switch checked={!!settings?.reviewerDemo?.enabled} onChange={e => update('reviewerDemo.enabled', e.target.checked)} />} label="Enable" />
            <TextField fullWidth label="Username" value={settings?.reviewerDemo?.username || ''} onChange={e => update('reviewerDemo.username', e.target.value)} sx={{ mb: 2 }} size="small" />
            <TextField fullWidth label="Password" value={settings?.reviewerDemo?.password || ''} onChange={e => update('reviewerDemo.password', e.target.value)} sx={{ mb: 2 }} size="small" />
            <TextField fullWidth label="Display Name" value={settings?.reviewerDemo?.displayName || ''} onChange={e => update('reviewerDemo.displayName', e.target.value)} sx={{ mb: 2 }} size="small" />
            <Divider sx={{ my: 1 }} />
            <FormControlLabel control={<Switch checked={!!settings?.reviewerDemo?.adminFeaturesEnabled} onChange={e => update('reviewerDemo.adminFeaturesEnabled', e.target.checked)} />} label="Admin Features" />
            <FormControlLabel control={<Switch checked={!!settings?.reviewerDemo?.enterpriseFeaturesEnabled} onChange={e => update('reviewerDemo.enterpriseFeaturesEnabled', e.target.checked)} />} label="Enterprise Features" />
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSave} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Save Changes'}</Button>
        <Button variant="outlined" onClick={fetchSettings} disabled={saving || loading}>Reload</Button>
      </Box>
    </Container>
  );
}

export default DemoSettings;

